import html2pdf from "html2pdf.js";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { SERVER_URL } from "../api";
import { toAbsoluteUrl } from "../_metronic/_helpers";
import { getTextById } from "../api/text";
import { getNameOwner, getNameSupplier } from "./helpers";

function htmlCreate(logo, artists, artworkImageUrl, artwork) {
  const html = `
        <div id="pdf-content" style="font-family: Helvetica, Arial, sans-serif; padding: 40px; max-width: 800px; margin: auto;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <img src="${logo}" style="height: 40px;" alt="Logo" crossorigin="anonymous">
          </div>
          <div style="text-align: center; margin-top: 40px;">
            <h1 style="font-size: 28px;">${artwork.title ||
              "Unknown Title"}</h1>
            <h2 style="font-size: 16px; color: #888;">${artists.find(
              (x) => x._id === artwork.artist
            )?.fullName || "---"}</h2>
          </div>
          <div style="text-align: center; margin: 30px auto;">
            <img src="${artworkImageUrl}" style="max-width: 100%; height: auto; max-height: 600px;" alt="Artwork image" crossorigin="anonymous">
          </div>
          <div style="display: flex; justify-content: space-between; margin-top: 30px;">
            <div style="width: 90%;">
              <div><strong>Year:</strong> ${artwork.year || "---"}</div>
              <div><strong>Size:</strong> ${artwork.size || "---"}</div>
              <div><strong>Technique:</strong> ${artwork.technique ||
                "Oil on Canvas (TBC)"}</div>
              <div><strong>Signature:</strong> ${artwork.signature ||
                "---"}</div>
              <div><strong>Catalogued:</strong> ${artwork.catalogued ||
                "---"}</div>
              <div><strong>Certified:</strong> ${artwork.certified ||
                "---"}</div>
              <div><strong>Auctions:</strong> ${artwork.recentAuctions ||
                "---"}</div>
            </div>
          </div>
        </div>
      `;
  return html;
}

// Función auxiliar para precargar imágenes
function preloadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous"; // Para evitar problemas de CORS
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

// Función auxiliar para esperar que todas las imágenes se carguen
function waitForImages(container) {
  const images = container.querySelectorAll("img");
  const imagePromises = Array.from(images).map((img) => {
    if (img.complete) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
    });
  });

  return Promise.all(imagePromises);
}

// Función para generar el PDF y devolverlo como blob
async function generatePDFBlob(
  artwork,
  artists,
  suppliers,
  owners,
  divisas,
  textContact,
  textFooter
) {
  // URLs de las imágenes
  const logo = toAbsoluteUrl("/media/logo/logo.png");
  const artworkImageUrl = SERVER_URL + "/" + artwork.imageURL;

  // Precargar imágenes para asegurar que estén disponibles
  await Promise.all([preloadImage(logo), preloadImage(artworkImageUrl)]);

  const html = htmlCreate(logo, artists, artworkImageUrl, artwork);

  // Crear contenedor temporal invisible
  const container = document.createElement("div");
  container.innerHTML = html;
  container.style.position = "absolute";
  container.style.left = "-9999px";
  container.style.top = "-9999px";
  document.body.appendChild(container);

  try {
    // Esperar a que todas las imágenes se carguen completamente
    await waitForImages(container);

    // Pequeña pausa adicional para asegurar el renderizado
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Generar el PDF como blob
    const element = container.querySelector("#pdf-content");

    const pdfBlob = await html2pdf()
      .set({
        margin: 10,
        filename: `${artwork.title || "artwork"}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          logging: false,
        },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      })
      .from(element)
      .outputPdf("blob");

    return pdfBlob;
  } finally {
    // Limpiar el DOM
    document.body.removeChild(container);
  }
}

// Función para descargar archivos y añadirlos al ZIP
async function addFilesToZip(zip, fileUrls, folderName) {
  if (!fileUrls || fileUrls.length === 0) return;

  const folder = zip.folder(folderName);
  const promises = fileUrls.map(async (fileUrl, index) => {
    try {
      let fileName = fileUrl.url.split("/").pop() || `archivo_${index + 1}`;
      fileName = fileName.replace(/^\d+-/, "");

      const response = await fetch(`${SERVER_URL}/${fileUrl.url}`);

      if (!response.ok) {
        console.warn(
          `No se pudo descargar: ${fileUrl.url} (${response.status})`
        );
        return;
      }

      const blob = await response.blob();
      folder.file(fileName, blob);
    } catch (error) {
      console.log(`Error al procesar ${fileUrl.url}:`, error);
    }
  });

  await Promise.all(promises);
}

export async function generateArtworkPDF(
  artwork,
  artists,
  suppliers,
  owners,
  divisas,
  images = false,
  videos = false,
  files = false
) {
  try {
    // Obtener textos
    const textContact = (await getTextById("679c9bb4474dd68f9a1080c6")).data
      .content;
    const textFooter = (await getTextById("68480f6d6bdf271d506a2cde")).data
      .content;

    // Si no se requieren archivos adicionales, solo generar y descargar el PDF
    if (!images && !videos && !files) {
      // Crear contenedor temporal invisible
      const container = document.createElement("div");

      const logo = toAbsoluteUrl("/media/logo/logo.png");
      const artworkImageUrl = SERVER_URL + "/" + artwork.imageURL;

      await Promise.all([preloadImage(logo), preloadImage(artworkImageUrl)]);

      const html = htmlCreate(logo, artists, artworkImageUrl, artwork);

      container.innerHTML = html;
      container.style.position = "absolute";
      container.style.left = "-9999px";
      container.style.top = "-9999px";
      document.body.appendChild(container);

      try {
        await waitForImages(container);
        await new Promise((resolve) => setTimeout(resolve, 500));

        const element = container.querySelector("#pdf-content");
        await html2pdf()
          .set({
            margin: 10,
            filename: `${artwork.title || "artwork"}.pdf`,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: {
              scale: 2,
              useCORS: true,
              allowTaint: false,
              logging: false,
            },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          })
          .from(element)
          .save();
      } finally {
        document.body.removeChild(container);
      }
      return;
    }

    // Si se requieren archivos adicionales, crear ZIP
    const zip = new JSZip();

    // Generar PDF y añadirlo al ZIP
    const pdfBlob = await generatePDFBlob(
      artwork,
      artists,
      suppliers,
      divisas,
      textContact,
      textFooter
    );
    zip.file(`${artwork.title || "artwork"}.pdf`, pdfBlob);

    // Añadir archivos según los parámetros booleanos
    const promises = [];

    if (images && artwork.imagesURLs && artwork.imagesURLs.length > 0) {
      promises.push(addFilesToZip(zip, artwork.imagesURLs, "images"));
    }

    if (videos && artwork.videosURLs && artwork.videosURLs.length > 0) {
      promises.push(addFilesToZip(zip, artwork.videosURLs, "videos"));
    }

    if (files && artwork.filesURLs && artwork.filesURLs.length > 0) {
      promises.push(addFilesToZip(zip, artwork.filesURLs, "files"));
    }

    // Esperar a que se procesen todos los archivos
    await Promise.all(promises);

    // Generar y descargar el ZIP
    const content = await zip.generateAsync({
      type: "blob",
      compression: "DEFLATE",
      compressionOptions: {
        level: 6,
      },
    });

    const zipName = `${artwork.title || "artwork"}_package.zip`;
    saveAs(content, zipName);
    console.log("Descarga completada");
  } catch (error) {
    console.error("Error generating PDF/ZIP:", error);
    alert("Error al generar el archivo. Revisa la consola para más detalles.");
    throw error;
  }
}
